import { IDeductionsByRetailerItem, ScoreCardTrend } from "@/core/repositories/analytics";
import {
  ANALYTICS_OTHERS_LABEL,
  MAX_TOP_ANALYTICS_ROWS,
  MIN_ANALYTICS_SHARE_PERCENT,
} from "./constants";

export interface RetailerChartItem {
  retailerName: string;
  amount: number;
  count: number;
  share: number;
  vsPrior: number | null;
  trend: ScoreCardTrend | null;
  isOther: boolean;
}

const isUnassignedRetailerRow = (item: IDeductionsByRetailerItem): boolean => {
  const retailer = item.retailer;
  if (!retailer) return true;

  const name = retailer.retailerName;
  return name == null || name === "";
};

const isActualOtherRetailer = (item: IDeductionsByRetailerItem): boolean => {
  const retailer = item.retailer;
  if (!retailer) return false;

  const name = retailer.retailerName?.toLowerCase();
  return name?.includes("other") ?? false;
};

const aggregateOthers = (rows: IDeductionsByRetailerItem[]): RetailerChartItem => ({
  retailerName: ANALYTICS_OTHERS_LABEL,
  amount: rows.reduce((total, row) => total + row.amount, 0),
  count: rows.reduce((total, row) => total + row.count, 0),
  share: rows.reduce((total, row) => total + row.share, 0),
  vsPrior: null,
  trend: null,
  isOther: true,
});

export const groupRetailersWithOther = (items: IDeductionsByRetailerItem[]): RetailerChartItem[] => {
  const unassignedRows = items.filter(item => isUnassignedRetailerRow(item));
  const assignableRows = items.filter(item => !isUnassignedRetailerRow(item) && !isActualOtherRetailer(item));
  const actualOtherRows = items.filter(item => isActualOtherRetailer(item));

  const eligible = assignableRows.filter(item => item.share >= MIN_ANALYTICS_SHARE_PERCENT);
  const belowThreshold = assignableRows.filter(item => item.share < MIN_ANALYTICS_SHARE_PERCENT);

  const sortedEligible = [...eligible].sort((first, second) => second.amount - first.amount);
  const topRows = sortedEligible.slice(0, MAX_TOP_ANALYTICS_ROWS);
  const overflowRows = sortedEligible.slice(MAX_TOP_ANALYTICS_ROWS);
  const othersSource = [...unassignedRows, ...overflowRows, ...belowThreshold, ...actualOtherRows];

  const mappedTopRows: RetailerChartItem[] = topRows.map(item => ({
    retailerName: item.retailer.retailerName!,
    amount: item.amount,
    count: item.count,
    share: item.share,
    vsPrior: item.vsPrior,
    trend: item.trend,
    isOther: false,
  }));

  if (othersSource.length === 0) {
    return mappedTopRows;
  }

  return [...mappedTopRows,
     aggregateOthers(othersSource)
    ];
};
